/**
 * 浏览器到后端的请求封装（控制台与管理后台共用）。
 *
 * 背景：控制台和管理后台与 identity、gateway_control、billing 同源部署，入口负载均衡按路径前缀分流
 *   （/auth /me /oauth → identity，/control → gateway_control，/billing → billing），
 *   登录态是 HttpOnly Cookie，前端拿不到令牌本身。
 * 设计意图：
 *   - 只用相对路径与 credentials: 'same-origin'，令牌不进 JS、不进 localStorage（PRD 5.1）。
 *   - 访问令牌 15 分钟（管理后台 10 分钟）过期，接口返回 401 时先调一次刷新接口再重试原请求；
 *     同一时刻只发一个刷新请求，其余请求排队等它（多个面板同时加载时不会并发轮换刷新令牌）。
 *   - 刷新返回 refresh_raced（另一个标签页刚刷新过）时等 300ms 直接重试原请求，新 Cookie 已经由那个标签页写好。
 *   - 刷新被后端拒绝（401、403 等 4xx）才调用 onAuthLost，应用据此回到登录页。
 *   - 刷新请求没发出去或后端返回 5xx（开发时后端重启、入口代理 502）只说明服务暂时连不上，会话还在：
 *     抛 service_unavailable，不调 onAuthLost。2026-11-03 之前两种情况混在一起，后端一重启就把人踢回登录页。
 * 关键约束：错误统一抛 ApiError（status、code、message），调用方按 code 映射文案，不直接展示英文 message。
 */

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: Record<string, unknown>

  constructor(status: number, code: string, message: string, detail: Record<string, unknown> = {}) {
    super(message)
    this.status = status
    this.code = code
    this.detail = detail
  }
}

export type Query = Record<string, string | number | boolean | null | undefined | string[]>

export interface HttpOptions {
  /** 刷新会话的接口路径：控制台 /auth/refresh，管理后台 /admin/auth/refresh。 */
  refreshPath: string
  /** 这些路径返回 401 时不触发刷新（登录、刷新本身）。 */
  noRefresh?: string[]
  onAuthLost?: () => void
}

const RETRY_CODES = new Set(['token_expired', 'not_logged_in', 'token_invalid'])

/**
 * 判断这个错误是否表示后端明确拒绝了登录态（没登录或没权限）。
 *
 * 背景：启动时用 /me 判断登录态。网络错误、5xx 只代表后端暂时连不上，不能据此当成未登录，
 *   否则后端重启期间打开页面就会被送回登录页、再输一遍密码和动态码。
 * 约束：只有 401、403 返回 true；其余（status 0 的网络错误、5xx、其他 4xx）调用方应当按「连不上」处理并重试。
 */
export function isAuthFailure(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 401 || e.status === 403)
}

function unavailable(): ApiError {
  return new ApiError(503, 'service_unavailable', 'Service is temporarily unavailable')
}

function buildUrl(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, x))
    else qs.append(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function parseError(resp: Response): Promise<ApiError> {
  let code = `http_${resp.status}`
  let message = resp.statusText || 'Request failed'
  let detail: Record<string, unknown> = {}
  try {
    const body = await resp.json()
    const err = body?.error
    if (err && typeof err === 'object') {
      code = String(err.code ?? code)
      message = String(err.message ?? message)
      detail = err
    } else if (typeof err === 'string') {
      // OAuth 规范格式：{"error": "...", "error_description": "..."}
      code = err
      message = String(body.error_description ?? message)
    }
  } catch {
    // 响应体不是 JSON（网关 502 页等），保留 http_<status>。
  }
  return new ApiError(resp.status, code, message, detail)
}

export interface Http {
  request<T>(method: string, path: string, opts?: { body?: unknown; query?: Query }): Promise<T>
  get<T>(path: string, query?: Query): Promise<T>
  post<T>(path: string, body?: unknown): Promise<T>
  put<T>(path: string, body?: unknown): Promise<T>
  patch<T>(path: string, body?: unknown): Promise<T>
  del<T>(path: string): Promise<T>
}

export function createHttp(options: HttpOptions): Http {
  let refreshing: Promise<'ok' | 'lost' | 'unavailable'> | null = null
  const noRefresh = new Set([options.refreshPath, ...(options.noRefresh ?? [])])

  /** 刷新会话：ok 已换到新 Cookie；lost 后端拒绝（会话失效）；unavailable 后端连不上，会话可能还在。 */
  async function refresh(): Promise<'ok' | 'lost' | 'unavailable'> {
    if (!refreshing) {
      refreshing = (async () => {
        try {
          const resp = await fetch(options.refreshPath, { method: 'POST', credentials: 'same-origin' })
          if (resp.ok) return 'ok'
          const err = await parseError(resp)
          if (err.code === 'refresh_raced') {
            await new Promise((r) => setTimeout(r, 300))
            return 'ok'
          }
          if (resp.status >= 500) {
            console.warn('[api] 会话刷新时后端不可用，保留登录态', err.status, err.code)
            return 'unavailable'
          }
          console.warn('[api] 会话刷新失败', err.status, err.code)
          return 'lost'
        } catch (e) {
          console.warn('[api] 会话刷新请求没有发出，保留登录态', e)
          return 'unavailable'
        } finally {
          setTimeout(() => {
            refreshing = null
          }, 0)
        }
      })()
    }
    return refreshing
  }

  async function send(method: string, url: string, body: unknown): Promise<Response> {
    const init: RequestInit = { method, credentials: 'same-origin', headers: { Accept: 'application/json' } }
    if (body !== undefined) {
      init.body = JSON.stringify(body)
      ;(init.headers as Record<string, string>)['Content-Type'] = 'application/json'
    }
    return fetch(url, init)
  }

  async function request<T>(method: string, path: string, opts: { body?: unknown; query?: Query } = {}): Promise<T> {
    const url = buildUrl(path, opts.query)
    let resp: Response
    try {
      resp = await send(method, url, opts.body)
    } catch (e) {
      console.error('[api] 网络错误', method, url, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    if (resp.status === 401 && !noRefresh.has(path)) {
      const err = await parseError(resp)
      if (!RETRY_CODES.has(err.code)) throw err
      const outcome = await refresh()
      if (outcome === 'unavailable') throw unavailable()
      if (outcome === 'lost') {
        options.onAuthLost?.()
        throw err
      }
      try {
        resp = await send(method, url, opts.body)
      } catch (e) {
        console.error('[api] 刷新后重试时网络错误', method, url, e)
        throw new ApiError(0, 'network_error', 'Network error')
      }
      if (resp.status === 401) {
        options.onAuthLost?.()
        throw await parseError(resp)
      }
    }
    if (!resp.ok) {
      const err = await parseError(resp)
      console.warn('[api] 请求失败', method, url, err.status, err.code)
      throw err
    }
    if (resp.status === 204) return undefined as T
    const text = await resp.text()
    return (text ? JSON.parse(text) : undefined) as T
  }

  return {
    request,
    get: (path, query) => request('GET', path, { query }),
    post: (path, body) => request('POST', path, { body: body ?? {} }),
    put: (path, body) => request('PUT', path, { body }),
    patch: (path, body) => request('PATCH', path, { body }),
    del: (path) => request('DELETE', path),
  }
}

/** 生成幂等用的随机键（下单 client_key、余额调整 ref）。 */
export function newKey(): string {
  return crypto.randomUUID()
}
